import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  Total,
  TotalTokensPublishedDetails
} from 'src/app/models/reporting/reporting.model';
import { createHttpParams } from 'src/app/shared/utils/create-http-params.utils';
import { environment } from '../../../environments/environment';

export abstract class BaseService<T> {
  protected resourceUrl: string;
  protected baseUrl: string;
  private dataSource = new BehaviorSubject(null as T);
  currentData = this.dataSource.asObservable();
  changeData(data: T) {
    this.dataSource.next(data);
  }

  constructor(protected http: HttpClient, entityName: string) {
    this.baseUrl = `${environment.api_url}`;
    this.resourceUrl = `${environment.api_url}/${entityName}`;
  }

  create(entity: T): Observable<HttpResponse<T>> {
    return this.http.post<T>(`${this.resourceUrl}`, entity, {
      observe: 'response'
    });
  }

  read(searchValues): Observable<HttpResponse<T[]>> {
    const params = createHttpParams(searchValues);
    return this.http.get<T[]>(this.resourceUrl, {
      params,
      observe: 'response'
    });
  }

  getTotalTokens(): Observable<HttpResponse<any>> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/token-statistics/tokens-exchanges`,
      {
        observe: 'response'
      }
    );
  }

  getTotals(): Observable<HttpResponse<any>> {
    return this.http.get<Total>(
      `${this.baseUrl}/admin/token-statistics/totals`,
      {
        observe: 'response'
      }
    );
  }

  getCcpTransactions(page): Observable<HttpResponse<any>> {
    return this.http.get<any>(
      `${this.baseUrl}/admin/ccp-transactions?page=${page}`,
      {
        observe: 'response'
      }
    );
  }

  getTotalTokensEmissions(page): Observable<HttpResponse<any>> {
    const params = createHttpParams(page);
    return this.http.get<any>(
      `${this.baseUrl}/admin/token-statistics/emissions`,
      {
        params,
        observe: 'response'
      }
    );
  }

  getTotalTokensDetails(id): Observable<HttpResponse<any>> {
    return this.http.get<TotalTokensPublishedDetails[]>(
      `${this.baseUrl}/admin/token-statistics/tokens-exchanges/${id}`,
      {
        observe: 'response'
      }
    );
  }

  find(id: number | string): Observable<HttpResponse<T>> {
    return this.http.get<T>(`${this.resourceUrl}/${id}`, {
      observe: 'response'
    });
  }

  export(searchValues: Record<string, any>): Observable<Blob> {
    const { page, size, ...exportParams } = searchValues;
    const params = createHttpParams(exportParams);
    return this.http.get<Blob>(`${this.resourceUrl}/export`, {
      params,
      responseType: 'blob' as 'json'
    });
  }

  update(entity: T, id?: number): Observable<HttpResponse<T>> {
    return this.http.put<T>(
      `${this.resourceUrl}${id ? '/' : ''}${id ? id : ''}`,
      entity,
      {
        observe: 'response'
      }
    );
  }

  delete(id: number): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${this.resourceUrl}/${id}`, {
      observe: 'response'
    });
  }
}
